import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, share, switchMap, take } from 'rxjs/operators';

import { ClearLocalStorage, Toast } from '@stylehaven/components';

import { CurrentUser, DeleteToast, selectAll, UnsetToasts } from '@digihaven/shared';

import { Logout } from '@actions/authentication.actions';
import { UnsetFunctionalities } from '@actions/functionality.action';
import { UnsetOrganisation } from '@actions/organisation.actions';
import { UnsetTokens } from '@actions/token.actions';
import { UnsetUser } from '@actions/user.actions';
import { AuthorizationService } from '@common/services/authorization/authorization.service';

import { ApplicationState } from 'state';

@Injectable()
export class BootstrapSandboxService {
    toasts$: Observable<Toast[]>;
    isAuthenticated$: Observable<boolean>;
    isTokenValid$: Observable<boolean>;

    constructor(
        private authorizationService: AuthorizationService,
        private store: Store<ApplicationState>
    ) {
        this.toasts$ = this.buildToastStream();

        this.isAuthenticated$ = this.buildIsAuthenticatedStream();
        this.isTokenValid$ = this.buildIsTokenValidStream();
    }

    deleteToast(toastId: number): void {
        this.store.dispatch(DeleteToast({ id: toastId }));
    }

    clearStore() {
        this.store.dispatch(new Logout());
        this.store.dispatch(new UnsetTokens());
        this.store.dispatch(new UnsetUser());
        this.store.dispatch(new UnsetFunctionalities());
        this.store.dispatch(new UnsetOrganisation());
        this.store.dispatch(UnsetToasts());

        ClearLocalStorage();
    }

    private buildToastStream(): Observable<Toast[]> {
        return this.store.select(state => selectAll(state.sharedToast));
    }

    private buildIsAuthenticatedStream(): Observable<boolean> {
        return this.store.select(state => state.authentication.isAuthenticated);
    }

    private buildIsTokenValidStream(): Observable<boolean> {
        return this.isAuthenticated$.pipe(
            take(1),
            switchMap(isAuthenticated => {
                if (isAuthenticated) {
                    return this.fetchUserInfo().pipe(
                        map(currentUser => !!currentUser),
                        catchError(() => of(false))
                    );
                } else {
                    return of(false);
                }
            }),
            share()
        );
    }

    private fetchUserInfo(): Observable<CurrentUser> {
        return this.authorizationService.fetchUserInfo();
    }
}
